import styled from '@emotion/styled';
import Link from 'next/link';

import { screen } from '@/components/common/breakpoints';
import { BulletPoints } from '@/components/common/bulletpoints';
import { SecondaryButton } from '@/components/common/Buttons';
import { RichHeading } from '@/components/common/Heading';
import { Heading, HeadingLevel } from '@/components/common/MarkUp';
import { Section } from '@/components/layout/Section';
import {
  getBulletPointColour,
  getButtonComponentTheme,
  getTextColour,
} from '@/lib/colourUtils';
import { PortableTextV2 } from '@/lib/sanity';

import type { SanityTwoColumnHeadingTextType } from '@/types/sanity';

export interface TwoColumnHeadingTextProps
  extends SanityTwoColumnHeadingTextType {}

export const TwoColumnHeadingText = ({
  heading,
  backgroundColour,
  text,
  ctaRight,
  ctaLeft,
  links,
}: TwoColumnHeadingTextProps) => {
  const backgroundColourToken = backgroundColour?.token;

  return (
    <Section
      backgroundColour={backgroundColourToken}
      colour={getTextColour(backgroundColourToken)}
      verticalPadding
    >
      <TwoColumnContainer>
        <HeadingLevel>
          <HeadingWrapper>
            <RichHeading heading={heading} size="headline-lg" />
          </HeadingWrapper>
          {!!ctaLeft?.ctaAction && (
            <ButtonContainer>
              <SecondaryButton
                componentTheme={getButtonComponentTheme(backgroundColourToken)}
                href={ctaLeft.ctaAction}
                aria-label={ctaLeft.buttonAccessibleLabel}
                className={
                  ctaLeft.trackingTag?.blockName &&
                  `tracking-${ctaLeft.trackingTag.blockName}__${ctaLeft.trackingTag.blockAction}`
                }
              >
                {ctaLeft.ctaLabel}
              </SecondaryButton>
            </ButtonContainer>
          )}
          <HeadingLevel>
            <TextContainer>
              {!!text && (
                <BulletPoints
                  bulletPointStyle="hexagon"
                  pointColour={getBulletPointColour(backgroundColourToken)}
                >
                  <PortableTextV2 value={text} />
                </BulletPoints>
              )}
              {!!ctaRight?.ctaAction && (
                <ButtonContainer>
                  <SecondaryButton
                    componentTheme={getButtonComponentTheme(
                      backgroundColourToken,
                    )}
                    href={ctaRight.ctaAction}
                    aria-label={ctaRight.buttonAccessibleLabel}
                    className={
                      ctaRight.trackingTag?.blockName &&
                      `tracking-${ctaRight.trackingTag.blockName}__${ctaRight.trackingTag.blockAction}`
                    }
                  >
                    {ctaRight.ctaLabel}
                  </SecondaryButton>
                </ButtonContainer>
              )}
              <Links>
                {!!links &&
                  links.map((link) => (
                    <StyledLink key={link._key}>
                      <Link href={link.ctaAction}>{link.ctaText}</Link>
                    </StyledLink>
                  ))}
              </Links>
            </TextContainer>
          </HeadingLevel>
        </HeadingLevel>
      </TwoColumnContainer>
    </Section>
  );
};

const TwoColumnContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto auto;
  gap: var(--spacing-large) var(--spacing-medium);

  ${screen.md} {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr;
  }
`;

const ButtonContainer = styled.div`
  grid-column: 1;
  grid-row: 3;
  margin-top: var(--spacing-xxx-large);

  a {
    margin: auto;
  }

  ${screen.md} {
    grid-row: 2;
    a {
      margin: 0;
    }
  }
`;

const TextContainer = styled.div`
  grid-column: 1;
  grid-row: 2;

  ${screen.md} {
    grid-column: 2;
    grid-row: 1;
  }

  * + ${Heading} {
    margin-top: var(--spacing-medium);
  }

  * + p {
    margin-top: var(--spacing-small);
  }
`;

const HeadingWrapper = styled.div`
  ${Heading} {
    text-align: left;
  }
`;

const Links = styled.div`
  > div:not(:last-child) {
    margin-bottom: calc(var(--spacing-300));
    ${screen.md} {
      margin-bottom: var(--spacing-500);
    }
  }
`;

const StyledLink = styled.div`
  a {
    text-decoration: none;
    color: inherit;
    font-size: var(--font-size-x-small);
  }
  &:hover {
    text-decoration: underline;
    text-underline-position: under;
  }
`;
